import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import TeamInviteWizard from '@/components/TeamInviteWizard';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, 
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger 
} from '@/components/ui/alert-dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Database, RefreshCw, AlertTriangle, Building2, Users, Plane, 
  Settings2, FileText, Shield, ClipboardCheck, GraduationCap, 
  Activity, Scale, History, CheckCircle, Loader2, Skull, FileDown,
} from 'lucide-react';
import { toast } from 'sonner';

const StatCard = ({ icon: Icon, label, value, color = "blue" }) => {
  const colors = {
    blue: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    green: "bg-green-500/10 text-green-400 border-green-500/20",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    purple: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    rose: "bg-rose-500/10 text-rose-400 border-rose-500/20",
    cyan: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
    slate: "bg-slate-500/10 text-slate-400 border-slate-500/20"
  };
  
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg border ${colors[color]}`}>
      <Icon className="w-5 h-5" />
      <div>
        <p className="text-xs text-slate-400">{label}</p>
        <p className="text-lg font-bold">{value}</p>
      </div>
    </div>
  );
};

export default function AdminDataManagementPage() {
  const { t } = useTranslation();
  const { api, user, token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reseeding, setReseeding] = useState(false);
  const [lastReseed, setLastReseed] = useState(null);

  // ---- Factory-reset state (customer pilot clean-slate) ---------------
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetPassword, setResetPassword] = useState('');
  const [resetKeepDemo, setResetKeepDemo] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetResult, setResetResult] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/data-stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      toast.error('Failed to load data statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleReseed = async () => {
    setReseeding(true);
    try {
      const response = await api.post('/admin/reseed-data');
      setStats(response.data.stats);
      setLastReseed(new Date().toISOString());
      toast.success('Data reseeded successfully!', {
        description: `Created ${response.data.stats.users} users, ${response.data.stats.aircraft} aircraft, ${response.data.stats.incidents} incidents`
      });
    } catch (error) {
      console.error('Reseed failed:', error);
      toast.error('Failed to reseed data', {
        description: error.response?.data?.detail || 'An error occurred'
      });
    } finally {
      setReseeding(false);
    }
  };

  // ---- Factory reset (pilot clean-slate) ------------------------------
  const canFactoryReset = resetConfirm.trim() === 'RESET' && resetPassword.length > 0 && !resetting;

  const handleFactoryReset = async () => {
    setResetting(true);
    setResetResult(null);
    try {
      const response = await api.post('/admin/factory-reset', {
        confirm: resetConfirm.trim(),
        password: resetPassword,
        keep_demo_users: resetKeepDemo,
      });
      setResetResult(response.data);
      toast.success('Factory reset complete — the workspace is now clean.', {
        description: `Deleted ${response.data.records_deleted} records across ${Object.keys(response.data.collections_purged || {}).length} collections.`,
      });
      setResetConfirm('');
      setResetPassword('');
      fetchStats();
    } catch (error) {
      console.error('Factory reset failed:', error);
      toast.error('Factory reset failed', {
        description: error.response?.data?.detail || 'An error occurred',
      });
    } finally {
      setResetting(false);
    }
  };

  // ---- Download the Day-1 Setup Report PDF ---------------------------
  const [downloadingKickoff, setDownloadingKickoff] = useState(false);
  const downloadKickoff = async () => {
    setDownloadingKickoff(true);
    try {
      const response = await api.get('/company/kickoff-pdf', { responseType: 'blob' });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'AeroGuard_Day1_Setup_Report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success('Day-1 Setup Report downloaded.');
    } catch (error) {
      toast.error('Download failed', { description: error.response?.data?.detail || error.message });
    } finally {
      setDownloadingKickoff(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="bg-slate-900 border-slate-800 p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-slate-400">This page is only accessible to administrators.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-data-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Database className="w-6 h-6 text-blue-400" />
            {t('common.manage')} {t('common.analytics')}
          </h1>
          <p className="text-slate-400 mt-1">{t('common.configuration')}</p>
        </div>
        <Button 
          variant="outline" 
          onClick={fetchStats}
          disabled={loading}
          className="border-slate-700"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          {t('common.refresh')}
        </Button>
      </div>

      {/* Warning Banner */}
      <Card className="bg-amber-950/30 border-amber-600/30">
        <CardContent className="p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-amber-300">Demonstration Data Environment</h3>
            <p className="text-amber-200/70 text-sm mt-1">
              This panel allows you to reset and reseed demonstration data. Reseeding will replace 
              existing data in core collections (organizations, users, aircraft, incidents, risks, 
              audits, CAPAs, etc.) with fresh sample data.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-blue-400" />
            Current Data Statistics
          </CardTitle>
          <CardDescription>Overview of records in each collection</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
            </div>
          ) : stats ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <StatCard icon={Building2} label="Organizations" value={stats.organizations} color="blue" />
              <StatCard icon={Users} label="Users" value={stats.users} color="green" />
              <StatCard icon={Plane} label="Aircraft" value={stats.aircraft} color="cyan" />
              <StatCard icon={Settings2} label="Engines" value={stats.engines} color="slate" />
              <StatCard icon={Settings2} label="Components" value={stats.components} color="slate" />
              <StatCard icon={FileText} label="Incidents" value={stats.incidents} color="rose" />
              <StatCard icon={Shield} label="Risks" value={stats.risks} color="amber" />
              <StatCard icon={ClipboardCheck} label="Audits" value={stats.audits} color="purple" />
              <StatCard icon={CheckCircle} label="CAPAs" value={stats.capas} color="green" />
              <StatCard icon={GraduationCap} label="Training" value={stats.training_programs} color="blue" />
              <StatCard icon={Activity} label="SPIs" value={stats.spis} color="cyan" />
              <StatCard icon={Scale} label="Reg. Clauses" value={stats.regulatory_clauses} color="purple" />
              <StatCard icon={Users} label="Memberships" value={stats.organization_memberships} color="green" />
              <StatCard icon={History} label="Audit Trails" value={stats.audit_trails} color="amber" />
            </div>
          ) : (
            <p className="text-slate-400 text-center py-8">No data available</p>
          )}
        </CardContent>
      </Card>

      {/* Reseed Section */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-blue-400" />
            Reset & Reseed Data
          </CardTitle>
          <CardDescription>
            Replace all demonstration data with fresh, interconnected sample data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-slate-950 rounded-lg p-4 border border-slate-800">
            <h4 className="font-medium text-white mb-3">What will be created:</h4>
            <ScrollArea className="h-[200px]">
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-400" />
                  <span><strong>3 Organizations</strong> - AeroGuard Aviation Group, MRO Services, Training Academy</span>
                </li>
                <li className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-green-400" />
                  <span><strong>11 Users</strong> - Admin, Safety Manager, Officers, Auditors, Inspector, etc.</span>
                </li>
                <li className="flex items-center gap-2">
                  <Plane className="w-4 h-4 text-cyan-400" />
                  <span><strong>6 Aircraft</strong> - A320-214, A321-251N, B777-300ER fleet</span>
                </li>
                <li className="flex items-center gap-2">
                  <Settings2 className="w-4 h-4 text-slate-400" />
                  <span><strong>12 Engines</strong> - CFM56, LEAP-1A32, GE90-115B linked to aircraft</span>
                </li>
                <li className="flex items-center gap-2">
                  <Settings2 className="w-4 h-4 text-slate-400" />
                  <span><strong>5 Components</strong> - APU, Landing Gear, FMC, Hydraulic Pump</span>
                </li>
                <li className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-rose-400" />
                  <span><strong>8 Incidents</strong> - Hard Landing, Bird Strike, TCAS RA, Ground Vehicle Incursion, etc.</span>
                </li>
                <li className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-400" />
                  <span><strong>5 Risks</strong> - Unstabilized Approach, Wildlife Strike, Hydraulic Reliability, etc.</span>
                </li>
                <li className="flex items-center gap-2">
                  <ClipboardCheck className="w-4 h-4 text-purple-400" />
                  <span><strong>5 Audits</strong> - SMS Compliance, Flight Ops, Ground Handling, MRO Quality, GACA</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span><strong>6 CAPAs</strong> - Linked to audits and incidents with proper workflow</span>
                </li>
                <li className="flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-blue-400" />
                  <span><strong>5 Training Programs</strong> - SMS Awareness, Hazard ID, Investigation, Auditor Training</span>
                </li>
                <li className="flex items-center gap-2">
                  <Activity className="w-4 h-4 text-cyan-400" />
                  <span><strong>6 SPIs</strong> - With 12 months of historical data points</span>
                </li>
                <li className="flex items-center gap-2">
                  <Scale className="w-4 h-4 text-purple-400" />
                  <span><strong>19 Regulatory Clauses</strong> - GACA GACAR Part 5, EASA, ICAO Annex 19</span>
                </li>
                <li className="flex items-center gap-2">
                  <History className="w-4 h-4 text-amber-400" />
                  <span><strong>80+ Audit Trails</strong> - Full history for every entity creation</span>
                </li>
              </ul>
            </ScrollArea>
          </div>

          <div className="bg-rose-950/30 rounded-lg p-4 border border-rose-600/30">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-400 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-rose-300">Warning: Destructive Action</h4>
                <p className="text-rose-200/70 text-sm mt-1">
                  This will <strong>delete all existing data</strong> in the affected collections and replace 
                  it with fresh demonstration data. This action cannot be undone.
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between pt-2">
            {lastReseed && (
              <p className="text-sm text-slate-400">
                Last reseeded: {new Date(lastReseed).toLocaleString()}
              </p>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button 
                  variant="destructive" 
                  disabled={reseeding}
                  className="bg-rose-600 hover:bg-rose-700"
                  data-testid="reseed-data-btn"
                >
                  {reseeding ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Reseeding...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Reset & Reseed All Data
                    </>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-slate-900 border-slate-800">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                    Confirm Data Reset
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-400">
                    Are you sure you want to reset and reseed all demonstration data? 
                    This will delete existing records in organizations, users, aircraft, 
                    incidents, risks, audits, CAPAs, and other core collections.
                    <br /><br />
                    <strong className="text-amber-400">This action cannot be undone.</strong>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction 
                    onClick={handleReseed}
                    className="bg-rose-600 hover:bg-rose-700"
                    data-testid="confirm-reseed-btn"
                  >
                    Yes, Reset & Reseed
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Company Profile — Day-1 Setup Report quick action */}
      <Card className="bg-slate-900 border-slate-800" data-testid="company-profile-card">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Building2 className="w-5 h-5 text-cyan-400" />
            Company Profile & Day-1 Setup Report
          </CardTitle>
          <CardDescription>
            Re-generate the one-page PDF you saw at the end of onboarding — hand it to your
            Accountable Executive and SMS committee.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button
            onClick={downloadKickoff}
            disabled={downloadingKickoff}
            variant="outline"
            className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
            data-testid="download-kickoff-pdf-btn"
          >
            {downloadingKickoff
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Preparing…</>
              : <><FileDown className="w-4 h-4 mr-2" /> Download Day-1 Setup Report (PDF)</>}
          </Button>
          <TeamInviteWizard token={token} />
        </CardContent>
      </Card>

      {/* Factory Reset — Customer Pilot Clean Slate */}
      <Card className="bg-slate-900 border-rose-800/60" data-testid="factory-reset-card">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Skull className="w-5 h-5 text-rose-400" />
            Factory Reset — Prepare for Customer Pilot
          </CardTitle>
          <CardDescription>
            Wipe ALL operational data (reports, audits, CAPAs, risks, fatigue reports, AI history)
            and start with a clean slate. System settings (roles, help content, ICAO categories,
            company profile) are preserved.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-md border border-emerald-600/30 bg-emerald-950/20 p-3 text-[12px]">
              <div className="font-semibold text-emerald-300 mb-1.5 flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" /> KEPT (system settings)
              </div>
              <ul className="text-emerald-100/70 space-y-0.5 list-disc pl-4">
                <li>Your admin account &amp; password</li>
                <li>Roles, permissions, LDAP mappings</li>
                <li>Help articles &amp; guided tours</li>
                <li>ICAO / GACAR / EASA reference clauses</li>
                <li>Notification templates &amp; preferences</li>
                <li>Company profile you just filled in</li>
                <li>SMS policies &amp; SPI definitions</li>
              </ul>
            </div>
            <div className="rounded-md border border-rose-600/40 bg-rose-950/20 p-3 text-[12px]">
              <div className="font-semibold text-rose-300 mb-1.5 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> WIPED (operational data)
              </div>
              <ul className="text-rose-100/70 space-y-0.5 list-disc pl-4">
                <li>All incidents, hazards, wildlife strikes</li>
                <li>All risks, CAPAs, audits, findings</li>
                <li>Fatigue reports, biometric &amp; FDM data</li>
                <li>AI query history, polish audit trails</li>
                <li>Meetings, bulletins, change requests</li>
                <li>Fleet: aircraft, engines, components</li>
                <li>Non-admin demo users (unless you choose to keep them)</li>
              </ul>
            </div>
          </div>

          <div className="rounded-md border border-amber-600/30 bg-amber-950/20 p-3 text-[12px] text-amber-100/80 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <b className="text-amber-200">This action cannot be undone.</b> Export any data you
              need before continuing. The reset writes a single entry to <code className="font-mono">audit_trails</code>
              so you can prove-out that the wipe happened.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="reset-confirm">Type <b className="text-rose-400 font-mono">RESET</b> to confirm</Label>
              <Input
                id="reset-confirm"
                data-testid="factory-reset-confirm"
                value={resetConfirm}
                onChange={(e) => setResetConfirm(e.target.value)}
                placeholder="RESET"
                className="bg-slate-950 border-slate-700 mt-1 font-mono"
              />
            </div>
            <div>
              <Label htmlFor="reset-password">Re-enter your admin password</Label>
              <Input
                id="reset-password"
                data-testid="factory-reset-password"
                type="password"
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
                placeholder="••••••••"
                className="bg-slate-950 border-slate-700 mt-1"
              />
            </div>
          </div>

          <label className="flex items-center gap-2 text-[12px] text-slate-300 cursor-pointer">
            <Checkbox
              checked={resetKeepDemo}
              onCheckedChange={(v) => setResetKeepDemo(!!v)}
              data-testid="factory-reset-keep-demo"
            />
            Also keep the seeded non-admin demo users (Safety Manager, Auditor, etc.) — leave unchecked to remove them.
          </label>

          <div className="flex items-center justify-between pt-2">
            {resetResult && (
              <p className="text-[12px] text-emerald-300 flex items-center gap-1.5">
                <CheckCircle className="w-3.5 h-3.5" />
                Deleted {resetResult.records_deleted} records &amp; {resetResult.users_removed} users at {new Date(resetResult.completed_at).toLocaleString()}
              </p>
            )}
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  disabled={!canFactoryReset}
                  className="bg-rose-700 hover:bg-rose-800 ml-auto"
                  data-testid="factory-reset-btn"
                >
                  {resetting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wiping…</>
                  ) : (
                    <><Skull className="w-4 h-4 mr-2" /> Factory reset — start fresh</>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-slate-900 border-rose-800/60">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-white flex items-center gap-2">
                    <Skull className="w-5 h-5 text-rose-400" /> Final confirmation
                  </AlertDialogTitle>
                  <AlertDialogDescription className="text-slate-300">
                    You&apos;re about to <b className="text-rose-400">permanently delete</b> every incident,
                    audit, CAPA, risk, and AI history entry currently in the system.
                    <br /><br />
                    The pilot customer will log in tomorrow to a clean workspace with only their
                    company profile and your admin account.
                    <br /><br />
                    <b className="text-amber-300">There is no undo.</b>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700">
                    Cancel — keep everything
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleFactoryReset}
                    className="bg-rose-700 hover:bg-rose-800"
                    data-testid="factory-reset-confirm-btn"
                  >
                    Yes — wipe operational data
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </CardContent>
      </Card>

      {/* Test Credentials */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="w-5 h-5 text-green-400" />
            Test Credentials
          </CardTitle>
          <CardDescription>Login credentials for testing different user roles</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { role: 'Admin', email: 'admin@aeroguard.sa', password: 'Admin@2024!', color: 'rose' },
              { role: 'Safety Manager', email: 'fatima.hassan@aeroguard.sa', password: 'Safety@2024!', color: 'blue' },
              { role: 'Auditor', email: 'mohammed.qahtani@aeroguard.sa', password: 'Quality@2024!', color: 'purple' },
              { role: 'GACA Inspector', email: 'inspector@gaca.gov.sa', password: 'Inspector@2024!', color: 'amber' }
            ].map(cred => (
              <div key={cred.email} className="bg-slate-950 rounded-lg p-4 border border-slate-800">
                <Badge variant="outline" className={`mb-2 text-${cred.color}-400 border-${cred.color}-400/30`}>
                  {cred.role}
                </Badge>
                <p className="text-sm text-slate-300 font-mono">{cred.email}</p>
                <p className="text-xs text-slate-500 font-mono mt-1">{cred.password}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
